import * as React from 'react';

import { cn } from '../lib/cn';

/**
 * Primary form/wizard actions that stay reachable on a phone.
 *
 * On desktop these buttons sit at the end of the content and that is fine — the
 * whole step is usually on screen. On a phone a wizard step is often several
 * screens tall, so "Continue" sat below everything: every step ended with a
 * scroll to the bottom purely to find the button. Here the bar pins to the
 * bottom of the viewport on small screens and reverts to an ordinary inline row
 * from `md` up.
 *
 * It parks ABOVE the app's fixed bottom tab bar (4rem + the home-indicator
 * inset) rather than at bottom:0, which would bury it under the navigation.
 *
 * Put the primary action LAST in the children, as you would in any trailing
 * button row: it then sits on the right in both layouts — desktop keeps
 * back-left/primary-right via `justify-between`, and on mobile the primary
 * simply takes the remaining width. (An earlier version reversed the row on
 * mobile to push the primary under the thumb, which also flipped desktop into
 * primary-left. Giving the primary `flex-1` gets the thumb reach without
 * touching either layout's reading order.)
 */
export interface StickyFormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Set when the surrounding screen has NO bottom tab bar (an overlay, or the
   * client portal's full-screen flows), so the bar sits flush to the bottom
   * inset instead of leaving a 4rem gap.
   */
  flush?: boolean;
  /**
   * Render the in-flow spacer that keeps page content clear of the pinned bar.
   * Default true, which is right whenever these actions are the last thing on
   * the page.
   *
   * Pass `false` when content follows the actions in the mobile flow — the
   * campaign wizard stacks its forecast rail after the form column, so the
   * built-in spacer would open a gap in the middle of the page while the rail's
   * tail still slid under the bar. Those pages render
   * `<StickyFormActionsSpacer />` at their true end instead.
   */
  spacer?: boolean;
}

/** The in-flow reserve for a pinned {@link StickyFormActions}; see `spacer`. */
export function StickyFormActionsSpacer() {
  return <div aria-hidden className="h-20 md:hidden" />;
}

export const StickyFormActions = React.forwardRef<HTMLDivElement, StickyFormActionsProps>(
  ({ className, children, flush = false, spacer = true, ...props }, ref) => (
    <>
      {/* The bar is fixed on mobile and therefore out of flow; without a reserve
          the last field sits underneath it. */}
      {spacer ? <StickyFormActionsSpacer /> : null}
      <div
        ref={ref}
        className={cn(
          // Mobile: pinned above the bottom tab bar.
          'fixed inset-x-0 z-[var(--tvl-z-sticky)]',
          flush
            ? 'bottom-[env(safe-area-inset-bottom)]'
            : 'bottom-[calc(4rem+env(safe-area-inset-bottom))]',
          'border-t border-[var(--tvl-hairline)] bg-[var(--tvl-canvas)] px-4 py-3',
          'flex items-center gap-2',
          // Desktop: an ordinary trailing row again.
          'md:static md:z-auto md:justify-between md:border-t-0 md:bg-transparent md:px-0 md:py-0',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </>
  ),
);
StickyFormActions.displayName = 'StickyFormActions';
